import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth';
import {
  initializeFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';
import { FIREBASE_CONFIG } from '@mdev/common';

const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _db: Firestore | null = null;
let _functions: Functions | null = null;

export const getApp = (): FirebaseApp => {
  if (!_app) {
    _app = initializeApp(FIREBASE_CONFIG);
  }
  return _app;
};

export const getFirebaseAuth = (): Auth => {
  if (!_auth) {
    _auth = getAuth(getApp());
    if (useEmulators) {
      connectAuthEmulator(_auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    }
  }
  return _auth;
};

export const getDb = (): Firestore => {
  if (!_db) {
    // `ignoreUndefinedProperties` makes writes drop `undefined` fields instead
    // of throwing "Unsupported field value: undefined". Firestore treats an
    // absent field and an undefined one identically, and readers already
    // default optional fields — so this matches our data model and prevents a
    // single stray undefined (e.g. an unset avatar/color) from blocking a write.
    _db = initializeFirestore(getApp(), { ignoreUndefinedProperties: true });
    if (useEmulators) {
      connectFirestoreEmulator(_db, '127.0.0.1', 8080);
    }
  }
  return _db;
};

export const getFns = (): Functions => {
  if (!_functions) {
    _functions = getFunctions(getApp());
    if (useEmulators) {
      connectFunctionsEmulator(_functions, '127.0.0.1', 5001);
    }
  }
  return _functions;
};
